import React, { useState, useEffect } from "react"
import { Button, Input, useTheme } from "@chakra-ui/react"
import Card from "../../common/components/Card"
import _ from "lodash"
import Measure from "react-measure"
import ReactMapboxGl from "react-mapbox-gl"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSpinner } from "@fortawesome/pro-light-svg-icons"
import { faMapMarkerAlt } from "@fortawesome/free-solid-svg-icons"
import { mapStyle, mapAccessToken, mapContainerStyle } from "../../config/map"
import MapPopup from "./MapPopup"
import MapMarker from "./MapMarker"
import usePrevious from "../../common/hooks/usePrevious"
import styles from "./css/EditMap.module.css"
import { Box } from "@chakra-ui/react"
import useColors from "../../common/hooks/useColors"

const Map = ReactMapboxGl({
  accessToken: mapAccessToken,
})

const EditMap = ({
  userLocation,
  mapLocation,
  zoom = 16,
  isEditing,
  markerToAdd,
  changePosition,
  getNearbyFeatures = () => {},
  selectFeature = () => {},
  isShowingGuide = false,
  isDisabled = false,
}) => {
  const { orangeColor } = useColors()
  const [isZoomOn, setIsZoomOn] = useState(!isEditing)
  const [isMapLoaded, setIsMapLoaded] = useState(false)
  const [showPopup, setShowPopup] = useState(false)
  const [popupCoords, setPopupCoords] = useState(null)
  const [popupMarker, setPopupMarker] = useState(null)
  const [mapDimensions, setMapDimensions] = useState({})
  const [textLat, setTextLat] = useState("")
  const [textLng, setTextLng] = useState("")
  const [mapZoom, setMapZoom] = useState([zoom])
  const [mapCoords, setMapCoords] = useState(
    mapLocation.coordinates && mapLocation.coordinates[0] !== 0
      ? mapLocation.coordinates.slice()
      : [userLocation.longitude, userLocation.latitude],
  )
  const prevUserLoc = usePrevious(userLocation)
  const prevMapLoc = usePrevious(mapLocation)
  useEffect(() => {
    if (
      mapLocation?.coordinates?.[0] === -106 &&
      (userLocation.latitude !== prevUserLoc?.latitude ||
        userLocation.longitude !== prevUserLoc?.longitude) &&
      !isEditing
    ) {
      setMapCoords([userLocation.longitude, userLocation.latitude])
    } else if (
      prevMapLoc &&
      mapLocation &&
      !_.isEqual(mapLocation.coordinates, prevMapLoc.coordinates)
    ) {
      setMapCoords(mapLocation.coordinates)
    } else if (!mapLocation && userLocation.latitude !== 0) {
      setMapCoords([userLocation.longitude, userLocation.latitude])
    }
  }, [userLocation, mapLocation, prevUserLoc, prevMapLoc, isEditing])
  const nearbyFeatures =
    getNearbyFeatures ?? {}
      ? Object.values(_.omit(getNearbyFeatures, "__typename"))?.flat()
      : []
  return (
    <Card p={0} overflow="hidden" variant="input">
      {isShowingGuide ? (
        <div className={styles["map-locator"]}>
          <div className={styles["horiz-map-line"]} />
          <div className={styles["vert-map-line"]} />
          <div className={styles["map-locator__icon"]}>
            <FontAwesomeIcon color={orangeColor} icon={faMapMarkerAlt} />
          </div>
        </div>
      ) : null}
      <Measure
        bounds
        onResize={(contentRect) => {
          setMapDimensions(contentRect.bounds)
        }}
      >
        {({ measureRef }) => (
          <Box ref={measureRef} position="relative">
            {!isDisabled && (
              <Button
                position="absolute"
                top={2}
                right={2}
                zIndex={10}
                size="sm"
                raised="true"
                variant="secondary"
                onClick={() => setIsZoomOn(!isZoomOn)}
                display="flex"
                alignItems={"center"}
              >
                {isZoomOn ? (
                  userLocation.latitude || userLocation.error ? (
                    "Disable Interaction"
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faSpinner} fixedWidth spin />
                      <span>&nbsp;&nbsp;&nbsp;Finding Location...</span>
                    </>
                  )
                ) : (
                  "Enable Interaction"
                )}
              </Button>
            )}
            {isZoomOn ? null : <div className={styles["map-zoom-disable"]} />}
            <Map
              style={mapStyle}
              center={isMapLoaded ? mapCoords : [0, 0]}
              zoom={mapZoom}
              movingMethod="jumpTo"
              containerStyle={{
                ...mapContainerStyle,
                width: mapDimensions.width,
              }}
              onStyleLoad={(map) => {
                setIsMapLoaded(true)
              }}
              onMove={(map) => {
                const center = map.getCenter()
                setMapCoords([center.lng, center.lat])
              }}
              onZoom={(map) => {
                setMapZoom([...[map.getZoom()]])
              }}
              onZoomEnd={(map) => {
                const center = map.getCenter()
                setTextLat(String(center.lat))
                setTextLng(String(center.lng))
              }}
              onMoveEnd={(map) => {
                const center = map.getCenter()
                const zm = map.getZoom()
                const bounds = map.getBounds()
                if (center.lng === 0 && center.lat === 0) {
                  return
                }
                setTextLat(String(center.lat))
                setTextLng(String(center.lng))

                setMapCoords([center.lng, center.lat])

                changePosition(
                  bounds,
                  {
                    type: "Point",
                    coordinates: [center.lng, center.lat],
                  },
                  zm,
                )
              }}
            >
              {
                /* load in any areas and guides based on current map position */
                nearbyFeatures &&
                  nearbyFeatures
                    .filter(
                      (feat) =>
                        !markerToAdd ||
                        feat?.slug !== markerToAdd.slug ||
                        feat?.type !== markerToAdd.type,
                    )
                    .map((feat) => (
                      <MapMarker
                        key={feat?.slug + feat?.type}
                        feature={feat}
                        onClick={(feat) => {
                          setShowPopup(true)
                          setPopupCoords(feat?.location.coordinates)
                          setPopupMarker(feat)
                          selectFeature(feat)
                        }}
                      />
                    ))
              }
              {/* {markerToAdd ? (
                <MapMarker
                  key={markerToAdd.slug + markerToAdd.type}
                  feature={markerToAdd}
                  isPrimary
                  onClick={feat => {
                    setShowPopup(true)
                    setPopupCoords(feat.location.coordinates)
                    setPopupMarker(feat)
                  }}
                />
              ) : null} */}
              {/* load in any areas and guides based on current map position */}
              {showPopup ? (
                <MapPopup
                  popupCoords={popupCoords}
                  popupMarker={popupMarker}
                  closePopup={() => {
                    setShowPopup(false)
                  }}
                />
              ) : null}
            </Map>
            <Box m={3} display="flex" alignItems="center">
              <Box mx={1} flex={1}>
                <Input
                  className="flex-1"
                  placeholder="Latitude"
                  value={textLat}
                  onChange={(e) => {
                    setTextLat(e.target.value)
                  }}
                />
              </Box>
              <Box mx={1} flex={1}>
                <Input
                  className="flex-1"
                  placeholder="Longitude"
                  value={textLng}
                  onChange={(e) => {
                    setTextLng(e.target.value)
                  }}
                />
              </Box>
              <Button
                variant="secondary"
                onClick={() => setMapCoords([textLng, textLat])}
              >
                Update
              </Button>
            </Box>
          </Box>
        )}
      </Measure>
    </Card>
  )
}

export default EditMap
